import Big from 'big.js';
import * as util from 'smg/util';

// Check if user prefers reduced motion
export function ShouldReduceMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Animation durations
const moneyAnimationDuration = ShouldReduceMotion() ? 0 : 1500;
const colorAnimationDuration = ShouldReduceMotion() ? 0 : 1200;
const positionAnimationDuration = ShouldReduceMotion() ? 0 : 800;

// Easing function for smooth animations
function easeOutQuart(t: number): number {
    return 1 - Math.pow(1 - t, 4);
}

// Animate a value change from one number to another
export class MoneyAnimation {
    #animationFrame?: number;

    constructor(
        private element: HTMLElement,
        private from: Big,
        private to: Big,
    ) { }

    animate(): Promise<void> {
        return new Promise(resolve => {
            if (ShouldReduceMotion() || this.from.eq(this.to)) {
                this.element.textContent = util.DollarFormat(this.to, false, true);
                resolve();
                return;
            }

            const startTime = performance.now();
            const difference = this.to.minus(this.from);

            const updateValue = (currentTime: number) => {
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / moneyAnimationDuration, 1);
                const easedProgress = easeOutQuart(progress);

                const currentValue = this.from.plus(difference.times(easedProgress));
                this.element.textContent = util.DollarFormat(currentValue, false, true);

                if (progress < 1) {
                    this.#animationFrame = requestAnimationFrame(updateValue);
                } else {
                    // Add a subtle pulse effect when animation completes if value increased
                    if (this.to.gt(this.from)) {
                        this.element.classList.add('value-increased');
                        setTimeout(() => this.element.classList.remove('value-increased'), 300);
                    }
                    resolve();
                }
            };

            this.#animationFrame = requestAnimationFrame(updateValue);
        });
    }

    cancel(): void {
        if (this.#animationFrame) {
            cancelAnimationFrame(this.#animationFrame);
        }
    }
}

// Animate background color transitions
export class ColorTransition {
    #animationFrame?: number;

    constructor(
        private element: HTMLElement,
        private fromColor: string,
        private toColor: string,
    ) { }

    animate(): Promise<void> {
        return new Promise(resolve => {
            if (ShouldReduceMotion() || this.fromColor === this.toColor) {
                this.element.style.backgroundColor = this.toColor;
                resolve();
                return;
            }

            const startTime = performance.now();

            // Parse RGB values from hex colors
            const fromRGB = this.#hexToRGB(this.fromColor);
            const toRGB = this.#hexToRGB(this.toColor);

            const updateColor = (currentTime: number) => {
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / colorAnimationDuration, 1);
                const easedProgress = easeOutQuart(progress);

                const r = Math.round(fromRGB.r + (toRGB.r - fromRGB.r) * easedProgress);
                const g = Math.round(fromRGB.g + (toRGB.g - fromRGB.g) * easedProgress);
                const b = Math.round(fromRGB.b + (toRGB.b - fromRGB.b) * easedProgress);

                this.element.style.backgroundColor = `rgb(${r}, ${g}, ${b})`;

                if (progress < 1) {
                    this.#animationFrame = requestAnimationFrame(updateColor);
                } else {
                    this.element.style.backgroundColor = this.toColor;
                    resolve();
                }
            };

            this.#animationFrame = requestAnimationFrame(updateColor);
        });
    }

    #hexToRGB(hex: string): { r: number; g: number; b: number } {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
        } : { r: 0, g: 0, b: 0 };
    }

    cancel(): void {
        if (this.#animationFrame) {
            cancelAnimationFrame(this.#animationFrame);
        }
    }
}

// Animate percentage value changes
export class PercentageAnimation {
    #animationFrame?: number;

    constructor(
        private element: HTMLElement,
        private from: Big,
        private to: Big,
    ) { }

    animate(): Promise<void> {
        return new Promise(resolve => {
            if (ShouldReduceMotion() || this.from.eq(this.to)) {
                this.element.textContent = `${this.to.toFixed(2)}%`;
                resolve();
                return;
            }

            const startTime = performance.now();
            const difference = this.to.minus(this.from);

            const updateValue = (currentTime: number) => {
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / moneyAnimationDuration, 1);
                const easedProgress = easeOutQuart(progress);

                const currentValue = this.from.plus(difference.times(easedProgress));
                this.element.textContent = `${currentValue.toFixed(2)}%`;

                if (progress < 1) {
                    this.#animationFrame = requestAnimationFrame(updateValue);
                } else {
                    resolve();
                }
            };

            this.#animationFrame = requestAnimationFrame(updateValue);
        });
    }

    cancel(): void {
        if (this.#animationFrame) {
            cancelAnimationFrame(this.#animationFrame);
        }
    }
}

// Animate row position changes
export class PositionAnimation {
    static async animateRows(tbody: HTMLElement, oldPositions: Map<string, number>, newPositions: Map<string, number>): Promise<void> {
        if (ShouldReduceMotion()) {
            return;
        }

        const rows = Array.from(tbody.querySelectorAll('tr'));
        const animations: Promise<void>[] = [];

        rows.forEach(row => {
            const teamID = row.getAttribute('data-team-id');
            if (!teamID) {
                return;
            }

            const oldPos = oldPositions.get(teamID);
            const newPos = newPositions.get(teamID);

            if (oldPos !== undefined && newPos !== undefined && oldPos !== newPos) {
                const rowHeight = row.getBoundingClientRect().height;
                const moveDistance = (oldPos - newPos) * rowHeight;

                // Set initial position
                row.style.transform = `translateY(${moveDistance}px)`;
                row.style.transition = `transform ${positionAnimationDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`;

                const promise = new Promise<void>(resolve => {
                    setTimeout(() => {
                        row.style.transform = 'translateY(0)';
                        setTimeout(() => {
                            row.style.transition = '';
                            resolve();
                        }, positionAnimationDuration);
                    }, 50); // Small delay to ensure initial position is set
                });

                animations.push(promise);
            }
        });

        await Promise.all(animations);
    }
}

// Track animation states to handle interruptions
export class AnimationController {
    #activeAnimations = new Set<{ cancel: () => void }>();

    addAnimation(animation: { cancel: () => void }): void {
        this.#activeAnimations.add(animation);
    }

    removeAnimation(animation: { cancel: () => void }): void {
        this.#activeAnimations.delete(animation);
    }

    cancelAll(): void {
        this.#activeAnimations.forEach(animation => animation.cancel());
        this.#activeAnimations.clear();
    }
}